import { Fragment, useContext, useMemo } from 'react';

import { Menu, Transition } from '@headlessui/react';
import { ClockIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Link from 'next/link';
import TimeAgo from 'react-timeago';

import { historyItemsMenuItems } from '../../constants/recentGenerations';
import HistoryContext from '../../context/history/context';
import { minuteFormatter } from '../../utils/timeStringFormatter';

interface RecentsProps {
  className?: string;
}

const getStatusDisplay = (status: string) => {
  const normalizedStatus = status.toLowerCase();

  switch (normalizedStatus) {
    case 'complete':
      return {
        text: 'Ready',
        className: 'text-green-700 font-medium',
        icon: CheckCircleIcon,
      };
    case 'incomplete':
      return {
        text: 'Interrupted',
        className: 'text-red-700 font-medium',
        icon: ExclamationTriangleIcon,
      };
    default:
      return {
        text: 'Unknown',
        className: 'text-gray-700 font-medium',
        icon: ExclamationTriangleIcon,
      };
  }
};

export default function Recents({ className }: RecentsProps) {
  const { historyItems } = useContext(HistoryContext);

  const historyItemsList = useMemo(
    () => historyItemsMenuItems(historyItems),
    [historyItems],
  );

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open, close }) => (
        <Fragment>
          <Menu.Button
            aria-label="Recently Generated"
            className={clsx(
              'flex h-full w-fit items-center justify-center gap-2 rounded-full border-2 border-blue-100 py-2 px-4 text-blue-600 hover:bg-blue-50',
              className,
            )}
          >
            <ClockIcon className="h-6 w-6" />
            <span className="hidden text-sm font-medium sm:block md:hidden lg:block">
              Recently Generated
            </span>
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
            show={open}
          >
            <Menu.Items
              static
              className="absolute -right-12 z-50 mt-1 w-80 rounded-md border border-gray-200 bg-white shadow-lg sm:left-0 sm:w-96"
            >
              <div className="py-2">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 pt-1 pb-3 text-lg font-medium text-gray-900">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-lg font-medium">Recent Generations</h2>
                    <p className="mr-6 text-sm font-light text-gray-500">
                      Your last 10 works will be saved temporarily on this
                      device.
                    </p>
                  </div>
                  <button
                    className="text-gray-600 hover:text-gray-900"
                    onClick={close}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto px-1">
                  {historyItemsList.map((item) => {
                    const statusDisplay = getStatusDisplay(item.status);

                    return (
                      <Menu.Item key={item.id}>
                        {({ active }) => (
                          <Link
                            href={item.href}
                            className={clsx(
                              'group  flex flex-row items-center justify-between gap-1 border-b-2 border-gray-100 px-4 py-3 text-base text-gray-700 last:border-b-0 hover:bg-gray-50 hover:text-gray-900',
                              { 'bg-gray-50': active },
                            )}
                          >
                            <div className="flex w-full items-start justify-between">
                              <div className="flex min-w-0 flex-1 flex-col gap-1 pr-4 font-medium">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="truncate font-medium capitalize text-gray-900"
                                    title={item.title}
                                  >
                                    {item.title || 'Untitled'}
                                  </span>
                                  <statusDisplay.icon
                                    aria-hidden="true"
                                    className={clsx(
                                      'h-4 w-4 flex-shrink-0 translate-y-[1.5px]',
                                      statusDisplay.className,
                                    )}
                                  />

                                  <span className={statusDisplay.className}>
                                    {statusDisplay.text}
                                  </span>
                                </div>
                                <div className="flex flex-row flex-nowrap gap-1.5 text-sm text-gray-500">
                                  {item.name && (
                                    <span className="max-w-48 truncate font-medium capitalize">
                                      {item.name}
                                    </span>
                                  )}
                                  •
                                  <span className="flex-shrink-0 text-sm text-gray-400">
                                    <TimeAgo
                                      date={item.timeDisplay}
                                      formatter={minuteFormatter}
                                    />
                                  </span>
                                </div>
                              </div>
                              <span className="flex-shrink-0 items-center rounded-full border-2 border-blue-100 px-4 py-0.5 text-sm text-blue-600 group-hover:border-blue-300 group-hover:bg-blue-100">
                                View
                              </span>
                            </div>
                          </Link>
                        )}
                      </Menu.Item>
                    );
                  })}
                </div>
                {historyItemsList.length === 0 && (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    You haven&apos;t worked on anything yet.
                  </div>
                )}
              </div>
            </Menu.Items>
          </Transition>
        </Fragment>
      )}
    </Menu>
  );
}
